// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * palette.ts — every colour that carries meaning, in one place.
 *
 * The contrast gate (tests/contrast.test.ts, principle #22) holds each of these
 * to ≥3:1 against every surface it is actually painted on. Inline colour
 * literals in the renderer are banned precisely so this file is the single
 * source the test can pin — a brick invisible against the sky is a shipped bug
 * (see scrapwall's 1.14:1 walls), and the only way to keep it out is to measure
 * the constants here AND probe the real pixels in the browser.
 *
 * Bricks are bright on a dark dusk sky, so every tint clears the sky by a wide
 * margin; adjacency between two touching bricks is carried by a dark OUTLINE
 * (also pinned ≥3:1 against every tint), never by hue alone.
 */

export const SKY_TOP = '#1b2f42';
export const SKY_BOTTOM = '#0e1a26';
/** The flat surface a brick is measured against — the darker of the sky band. */
export const SKY = '#12202e';

export const PLINTH = '#c9a26b';
export const PLINTH_EDGE = '#8a6a3c';

export const GOAL = '#f4f9ff';      // the height line to race for (Ascent) — a near-white UI line
export const KILL = '#e06a78';      // the line a brick is lost below

/** Gust / wind streak colour. */
export const GUST = '#9fd8ff';

export const TEXT = '#eaf2f8';
export const MUTED = '#93a7bb';

/** Dark seam drawn around every brick so neighbours never merge. */
export const BRICK_LINE = '#0a1119';

/**
 * The five brick tints. Chosen so each is ≥3:1 on the sky AND the outline is
 * ≥3:1 on each of them, and they are told apart by luminance as well as hue
 * (colour-blind safe).
 */
export const BRICK_TINTS: readonly string[] = [
  '#f2a94e', // amber
  '#45c2b0', // teal
  '#ef7a5f', // coral
  '#b394f2', // violet
  '#a9d65a', // lime
];

/** A brick's fill from its tint index. */
export function brickColor(tintIndex: number): string {
  return BRICK_TINTS[((tintIndex % BRICK_TINTS.length) + BRICK_TINTS.length) % BRICK_TINTS.length];
}

// --- WCAG contrast maths (used by tests/contrast.test.ts + the pixel probe) --
function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). Throws on bad input. */
export function luminance(hex: string): number {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) throw new Error(`not a #rrggbb colour: ${hex}`);
  const n = parseInt(m[1], 16);
  const r = channel((n >> 16) & 255);
  const g = channel((n >> 8) & 255);
  const b = channel(n & 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}